// src/features/analytics/components/PlaybackTrendCard.tsx

import { useState } from 'react';
import { Group, Paper, SegmentedControl, Skeleton, Text, Title } from '@mantine/core';
import { AreaChart } from '@mantine/charts';
import {
  ANALYTICS_METRICS,
  ANALYTICS_METRIC_LABELS,
  type AnalyticsMetric,
} from '@/lib/constants/analytics';
import type { UtcRange } from '@/shared/utils/utcRange';
import { AnalyticsUtils } from '../AnalyticsUtils';
import { fillDailyGaps, fillableRange, trendTickInterval, utcDayLabel } from '../playbackTrend';
import { AnalyticsCoverageNotice } from './AnalyticsCoverageNotice';
import { AnalyticsErrorState } from './AnalyticsErrorState';
import type {
  AnalyticsEnvelope,
  OverviewPlayback,
  PlaybackDailyData,
  PlaybackDailyPoint,
} from '../types';

interface PlaybackTrendCardProps {
  playbackDaily: AnalyticsEnvelope<PlaybackDailyData> | null;
  range: UtcRange;
  /**
   * Overview playback totals for the same range — the range-total annotation
   * must come from the range-wide state merge, NOT a client-side sum of the
   * daily points: sessions/uniqueViewers are uniq states, so summing per-day
   * merges double-counts midnight-spanning sessions (verified in prod: summed
   * daily sessions ran ~1.6% above the overview total). Absent → no annotation.
   */
  rangeTotals: OverviewPlayback | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  onRetry: () => void;
}

interface TrendChartRow {
  day: string;
  value: number;
  sessions: number;
  watchTimeMs: number;
  uniqueViewers: number;
}

function chartValue(point: PlaybackDailyPoint, metric: AnalyticsMetric): number {
  if (metric === 'sessions') return point.sessions;
  if (metric === 'uniqueViewers') return point.uniqueViewers;
  return Math.round(point.watchTimeMs / 60000); // charted in whole minutes, like the audience chart
}

function metricTotalLabel(totals: OverviewPlayback, metric: AnalyticsMetric): string {
  if (metric === 'sessions') return `${AnalyticsUtils.formatCount(totals.sessions)} sessions`;
  if (metric === 'uniqueViewers')
    return `${AnalyticsUtils.formatCount(totals.uniqueViewers)} unique viewers`;
  return `${AnalyticsUtils.formatWatchTime(totals.watchTimeMs)} watched`;
}

/** All three metrics for the hovered UTC day — the payload row carries them all. */
function TrendTooltip({ label, payload }: { label?: unknown; payload?: unknown }) {
  const rows = payload as { payload?: TrendChartRow }[] | undefined;
  const row = rows?.[0]?.payload;
  if (!row) return null;
  return (
    <Paper withBorder shadow="sm" p="xs">
      <Text size="xs" fw={600}>
        {String(label)} (UTC)
      </Text>
      <Text size="xs">Sessions: {AnalyticsUtils.formatCount(row.sessions)}</Text>
      <Text size="xs">Watch time: {AnalyticsUtils.formatWatchTime(row.watchTimeMs)}</Text>
      <Text size="xs">Unique viewers: {AnalyticsUtils.formatCount(row.uniqueViewers)}</Text>
    </Paper>
  );
}

/**
 * "Views over time" — the daily playback trend. One metric charted at a time;
 * the response carries all three per point, so toggling never refetches. Days
 * the API omitted are zero-filled client-side (fillableRange keeps future and
 * pre-coverage days out), and the empty state is honest per OverviewCards
 * conventions.
 */
export function PlaybackTrendCard({
  playbackDaily,
  range,
  rangeTotals,
  isLoading,
  isError,
  error,
  onRetry,
}: PlaybackTrendCardProps) {
  const [metric, setMetric] = useState<AnalyticsMetric>('sessions');

  const points = playbackDaily?.data.points ?? [];
  const filled = fillDailyGaps(
    points,
    fillableRange(range, playbackDaily?.coverage, Math.floor(Date.now() / 1000))
  );
  const chartData: TrendChartRow[] = filled.map((p) => ({
    day: utcDayLabel(p.day),
    value: chartValue(p, metric),
    sessions: p.sessions,
    watchTimeMs: p.watchTimeMs,
    uniqueViewers: p.uniqueViewers,
  }));

  return (
    <Paper withBorder p="md">
      <Group justify="space-between" mb="xs" wrap="wrap">
        <Title order={5}>Views over time</Title>
        {points.length > 0 && (
          <Group gap="sm" wrap="wrap">
            {rangeTotals && (
              <Text size="xs" c="dimmed">
                Range total: {metricTotalLabel(rangeTotals, metric)}
              </Text>
            )}
            <SegmentedControl
              size="xs"
              data={ANALYTICS_METRICS.map((m) => ({
                value: m,
                label: ANALYTICS_METRIC_LABELS[m],
              }))}
              value={metric}
              onChange={(value) => setMetric(value as AnalyticsMetric)}
            />
          </Group>
        )}
      </Group>
      {isLoading ? (
        <Skeleton height={220} />
      ) : isError ? (
        <AnalyticsErrorState error={error} onRetry={onRetry} />
      ) : points.length === 0 ? (
        <AnalyticsCoverageNotice
          coverage={playbackDaily?.coverage}
          freshness={playbackDaily?.freshness}
          range={range}
          emptyMessage="No playback data for this range. Playback analytics begins with the new player rollout."
        />
      ) : (
        <>
          <AnalyticsCoverageNotice
            coverage={playbackDaily?.coverage}
            freshness={playbackDaily?.freshness}
            range={range}
          />
          <AreaChart
            h={220}
            data={chartData}
            dataKey="day"
            series={[
              {
                name: 'value',
                label:
                  metric === 'watchTime'
                    ? 'Watch time (min)'
                    : ANALYTICS_METRIC_LABELS[metric],
                color: 'blue.6',
              },
            ]}
            curveType="monotone"
            withDots={false}
            xAxisProps={{ interval: trendTickInterval(filled.length) }}
            tooltipProps={{
              content: ({ label, payload }) => <TrendTooltip label={label} payload={payload} />,
            }}
          />
          <Text size="xs" c="dimmed" mt={4}>
            Daily {ANALYTICS_METRIC_LABELS[metric].toLowerCase()} per UTC day.
          </Text>
        </>
      )}
    </Paper>
  );
}
